import type { RouteObject } from 'react-router-dom'
import {
  CalendarCheck,
  Package,
  MoonStars,
  ListBullets,
  Notebook,
  Tag,
} from '@phosphor-icons/react'
import { ROLES } from '@/auth/roles'
import type { PhoneTileDef } from '@/features/phone/tiles'
import './commands'
import { GateDayPage } from './pages/GateDayPage'
import { OpenDayPage } from './pages/OpenDayPage'
import { DeliveryPage } from './pages/delivery/DeliveryPage'
import { DeliveryListPage } from './pages/DeliveryListPage'
import { CloseDayPage } from './pages/CloseDayPage'
import { PaperDayPage } from './pages/PaperDayPage'
import { RetagPage } from './pages/RetagPage'
import { ByScope } from './v1/ByScope'
import { V1DeliveryPage } from './v1/V1Delivery'
import { V1CloseDay, V1Home, V1OpenDay, V1PayStatus } from './v1/V1Day'
import { V1PaperDay } from './v1/V1Paper'
import { V1Retag } from './v1/V1Retag'
import { V1BankPage } from './v1/bank/V1Bank'
import { V1Village } from './v1/V1Village'

/**
 * Farm gate (F4) phone flows, mounted under /m (behind device-ready + PIN unlock).
 *   /m/gate               day status (roster, open day, tags in hand, spare roll, void tag, sync)
 *   /m/gate/open-day      F4.1
 *   /m/gate/delivery/new  F4.2 → F4.10 (a new farmer; ?id= resumes one of the open farmers), F4-B1, F4-B2
 *   /m/gate/deliveries    the day's deliveries with server state (F4.9-l)
 *   /m/gate/close-day     F4.11
 *   /m/gate/paper         F4-B3 paper day back-entry
 *   /m/gate/retag         F4.6-h supervisor re-tag
 *   /m/gate/bank          V1: verify and add a farmer's passbook account
 *   /m/gate/village       V1: pick the village to work in (multi-village phones), after the PIN
 *
 * Seva Mandir V1 (roster config.scope) draws these on the alignment map's V1 boards
 * (features/gate/v1); /m/gate/deliveries is then "पैसा आया?".
 */
export const gateRoutes: RouteObject[] = [
  { path: 'gate', element: <ByScope v1={<V1Home />} full={<GateDayPage />} /> },
  { path: 'gate/open-day', element: <ByScope v1={<V1OpenDay />} full={<OpenDayPage />} /> },
  {
    path: 'gate/delivery/new',
    element: <ByScope v1={<V1DeliveryPage />} full={<DeliveryPage />} />,
  },
  {
    path: 'gate/deliveries',
    element: <ByScope v1={<V1PayStatus />} full={<DeliveryListPage />} />,
  },
  { path: 'gate/close-day', element: <ByScope v1={<V1CloseDay />} full={<CloseDayPage />} /> },
  { path: 'gate/paper', element: <ByScope v1={<V1PaperDay />} full={<PaperDayPage />} /> },
  { path: 'gate/retag', element: <ByScope v1={<V1Retag />} full={<RetagPage />} /> },
  { path: 'gate/bank', element: <ByScope v1={<V1BankPage />} full={<V1BankPage />} /> },
  { path: 'gate/village', element: <V1Village /> },
]

const GATE_ROLES = [ROLES.VILLAGE_OPERATOR, ROLES.SUPERVISOR]

export const gateTiles: PhoneTileDef[] = [
  {
    to: '/m/gate',
    title: 'gate.day.title',
    hint: 'gate.day.hint',
    icon: CalendarCheck,
    team: 'VILLAGE',
    roles: GATE_ROLES,
  },
  {
    to: '/m/gate/delivery/new',
    title: 'gate.newDelivery',
    hint: 'gate.newDeliveryHint',
    icon: Package,
    team: 'VILLAGE',
    roles: GATE_ROLES,
    primary: true,
  },
  {
    to: '/m/gate/deliveries',
    title: 'gate.list.title',
    hint: 'gate.list.hint',
    icon: ListBullets,
    team: 'VILLAGE',
    roles: GATE_ROLES,
  },
  {
    to: '/m/gate/close-day',
    title: 'gate.closeDay',
    hint: 'gate.closeDayHint',
    icon: MoonStars,
    team: 'VILLAGE',
    roles: GATE_ROLES,
  },
  {
    to: '/m/gate/paper',
    title: 'gate.paper.title',
    hint: 'gate.paper.hint',
    icon: Notebook,
    team: 'VILLAGE',
    roles: GATE_ROLES,
  },
  {
    to: '/m/gate/retag',
    title: 'gate.retag.title',
    hint: 'gate.retag.hint',
    icon: Tag,
    team: 'VILLAGE',
    roles: [ROLES.SUPERVISOR],
  },
]
